import { useState, useCallback, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { toast } from 'sonner'

const STORAGE_KEY = 'time_tracker_active'

function readLS() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function writeLS(sessaoId, projetoId, resumedAt) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ sessaoId, projetoId, resumedAt }))
}

function clearLS() { localStorage.removeItem(STORAGE_KEY) }

export function useTimeTracker() {
  const { user } = useAuth()
  const [sessaoAtiva, setSessaoAtiva] = useState(null)
  const [seconds, setSeconds] = useState(0)
  const timerRef = useRef(null)
  const resumedAtRef = useRef(null) // timestamp (ms) when timer was last resumed
  const baseSecondsRef = useRef(0)  // duracao_segundos acumulada antes do último resume

  const projetoIdAtivo = sessaoAtiva?.projeto_id ?? null
  const isRunning = !!sessaoAtiva && !sessaoAtiva.pausado_em

  // ── Timer engine ────────────────────────────────────────
  const startTimer = useCallback((baseSec, resumedAt) => {
    clearInterval(timerRef.current)
    baseSecondsRef.current = baseSec
    resumedAtRef.current = resumedAt
    const elapsed = baseSec + Math.floor((Date.now() - resumedAt) / 1000)
    setSeconds(elapsed)
    timerRef.current = setInterval(() => {
      const now = Date.now()
      setSeconds(baseSecondsRef.current + Math.floor((now - resumedAtRef.current) / 1000))
    }, 1000)
  }, [])

  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current)
    timerRef.current = null
  }, [])

  // ── Visibility change — recalc on tab focus (laptop sleep) ──
  useEffect(() => {
    function onVisibility() {
      if (document.visibilityState !== 'visible') return
      if (!resumedAtRef.current || !timerRef.current) return
      setSeconds(baseSecondsRef.current + Math.floor((Date.now() - resumedAtRef.current) / 1000))
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  // ── Recover from localStorage + DB fallback (cross-device) ──
  useEffect(() => {
    if (!user) return
    async function recover() {
      // 1. Tenta recuperar do localStorage (mesmo device)
      const ls = readLS()
      if (ls?.sessaoId) {
        const { data, error } = await supabase
          .from('sessoes_trabalho')
          .select('id, projeto_id, user_id, iniciado_em, pausado_em, duracao_segundos, idle_detectada')
          .eq('id', ls.sessaoId)
          .is('pausado_em', null)
          .single()
        if (!error && data) {
          setSessaoAtiva(data)
          const resumedAt = ls.resumedAt || new Date(data.iniciado_em).getTime()
          startTimer(data.duracao_segundos, resumedAt)
          return
        }
        clearLS()
      }

      // 2. Fallback: busca sessão ativa no banco (outro device pode ter iniciado)
      const { data: dbSession } = await supabase
        .from('sessoes_trabalho')
        .select('id, projeto_id, user_id, iniciado_em, pausado_em, duracao_segundos, idle_detectada')
        .eq('user_id', user.id)
        .is('pausado_em', null)
        .single()
      if (dbSession) {
        setSessaoAtiva(dbSession)
        const resumedAt = new Date(dbSession.iniciado_em).getTime()
        writeLS(dbSession.id, dbSession.projeto_id, resumedAt)
        startTimer(dbSession.duracao_segundos, resumedAt)
      }
    }
    recover()
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Cleanup ──
  useEffect(() => () => clearInterval(timerRef.current), [])

  // ── Pause current session (internal) ────────────────────
  const _pauseCurrent = useCallback(async (opts = {}) => {
    if (!sessaoAtiva || sessaoAtiva.pausado_em) return
    stopTimer()
    const now = Date.now()
    const extra = resumedAtRef.current ? Math.floor((now - resumedAtRef.current) / 1000) : 0
    const totalSec = (sessaoAtiva.duracao_segundos || 0) + extra
    const updates = {
      pausado_em: new Date(now).toISOString(),
      duracao_segundos: totalSec,
      ...(opts.idle ? { idle_detectada: true } : {}),
    }
    await supabase.from('sessoes_trabalho').update(updates).eq('id', sessaoAtiva.id)
    clearLS()
    setSessaoAtiva(null)
    setSeconds(0)
  }, [sessaoAtiva, stopTimer])

  // ── Play ────────────────────────────────────────────────
  const play = useCallback(async (projetoId) => {
    if (!user) return
    // Auto-pause different project
    if (sessaoAtiva && !sessaoAtiva.pausado_em && sessaoAtiva.projeto_id !== projetoId) {
      await _pauseCurrent()
    }
    // If same project is already running, ignore
    if (sessaoAtiva && !sessaoAtiva.pausado_em && sessaoAtiva.projeto_id === projetoId) return

    const now = Date.now()
    const { data, error } = await supabase
      .from('sessoes_trabalho')
      .insert({ projeto_id: projetoId, user_id: user.id })
      .select('id, projeto_id, user_id, iniciado_em, pausado_em, duracao_segundos, idle_detectada')
      .single()
    if (error) {
      // Unique index conflict — another tab may have an active session
      if (error.code === '23505') {
        const { data: existing } = await supabase
          .from('sessoes_trabalho').select('id, projeto_id, user_id, iniciado_em, pausado_em, duracao_segundos, idle_detectada')
          .eq('user_id', user.id).is('pausado_em', null).single()
        if (existing) {
          if (existing.projeto_id === projetoId) {
            setSessaoAtiva(existing)
            const resumedAt = new Date(existing.iniciado_em).getTime()
            writeLS(existing.id, projetoId, resumedAt)
            startTimer(existing.duracao_segundos, resumedAt)
            return
          }
          // Pause the foreign session, then retry
          await supabase.from('sessoes_trabalho').update({
            pausado_em: new Date().toISOString(),
            duracao_segundos: existing.duracao_segundos + Math.floor((Date.now() - new Date(existing.iniciado_em).getTime()) / 1000),
          }).eq('id', existing.id)
          const { data: retry } = await supabase
            .from('sessoes_trabalho').insert({ projeto_id: projetoId, user_id: user.id })
            .select('id, projeto_id, user_id, iniciado_em, pausado_em, duracao_segundos, idle_detectada').single()
          if (retry) {
            setSessaoAtiva(retry)
            writeLS(retry.id, projetoId, now)
            startTimer(0, now)
            return
          }
        }
        toast.error('Não foi possível iniciar sessão')
        return
      }
      toast.error('Erro ao iniciar sessão')
      return
    }
    setSessaoAtiva(data)
    writeLS(data.id, projetoId, now)
    startTimer(0, now)
  }, [user, sessaoAtiva, _pauseCurrent, startTimer])

  // ── Pause (public) ──────────────────────────────────────
  const pause = useCallback(async (opts = {}) => {
    await _pauseCurrent(opts)
  }, [_pauseCurrent])

  // ── Update sessão encerrada (editar horários) ──────────
  const updateSessao = useCallback(async (sessaoId, campos) => {
    // Se mudou horários, recalcular duração
    if (campos.iniciado_em || campos.pausado_em) {
      const { data: current } = await supabase
        .from('sessoes_trabalho').select('iniciado_em, pausado_em').eq('id', sessaoId).single()
      if (current) {
        const inicio = new Date(campos.iniciado_em ?? current.iniciado_em)
        const fim    = new Date(campos.pausado_em ?? current.pausado_em)
        if (fim > inicio) {
          campos.duracao_segundos = Math.floor((fim - inicio) / 1000)
        }
      }
    }
    const { error } = await supabase.from('sessoes_trabalho').update(campos).eq('id', sessaoId)
    if (error) { toast.error('Erro ao atualizar sessão'); return false }
    return true
  }, [])

  // ── Deletar sessão de trabalho ────────────────────────
  const deleteSessao = useCallback(async (sessaoId) => {
    const { error } = await supabase.from('sessoes_trabalho').delete().eq('id', sessaoId)
    if (error) { toast.error('Erro ao excluir sessão'); return false }
    toast.success('Sessão excluída')
    return true
  }, [])

  return { sessaoAtiva, projetoIdAtivo, seconds, isRunning, play, pause, updateSessao, deleteSessao }
}
